import {
  withdrawalMethodOptions,
  withdrawalStatusOptions
} from '@library/component-utils/filters';
import { Filter } from '@library/components/filters';
import { Icon } from '@library/components/icons/Icon';
import { StatusIcon } from '@library/components/StatusIcon';
import type { WithdrawalsFilters } from '@library/types/filters';
import bankIcon from '@library/assets/images/icons/bank.svg';
import crediCardIcon from '@library/assets/images/icons/credit-card.svg';
import { useMemo } from 'react';

export interface PaymentsWithdrawalsFiltersProps {
  className?: string;
  containerClassName?: string;
  filters: WithdrawalsFilters;
  onWithdrawalStatusChange?: (value: string) => void;
  onWithdrawalMethodChange?: (value: string) => void;
  showOptionIcons?: boolean;
}

export const PaymentsWithdrawalsFilters = ({
  className = '',
  containerClassName = '',
  filters,
  onWithdrawalStatusChange = () => {},
  onWithdrawalMethodChange = () => {},
  showOptionIcons = false
}: PaymentsWithdrawalsFiltersProps) => {
  const { withdrawalMethod, withdrawalStatus } = filters;

  const statusOptionsWithIcons = useMemo(
    () =>
      withdrawalStatusOptions.map((option) => ({
        ...option,
        icon: <StatusIcon status={option.key} classes="!size-2 !my-1" />
      })),
    []
  );

  const methodOptionsWithIcons = useMemo(
    () =>
      withdrawalMethodOptions.map((option) => {
        const isBank = option.name.toLowerCase().includes('bank');

        return {
          ...option,
          icon: (
            <Icon
              alt={isBank ? 'Bank' : 'Credit Card'}
              src={isBank ? bankIcon : crediCardIcon}
              className="mr-1"
            />
          )
        };
      }),
    []
  );

  const statusOptions = useMemo(
    () => (showOptionIcons ? statusOptionsWithIcons : withdrawalStatusOptions),
    [showOptionIcons, statusOptionsWithIcons]
  );

  const methodOptions = useMemo(
    () => (showOptionIcons ? methodOptionsWithIcons : withdrawalMethodOptions),
    [showOptionIcons, methodOptionsWithIcons]
  );

  return (
    <div
      className="inline-flex gap-4 text-white"
      data-testid="PaymentsWithdrawalsFilters"
    >
      <Filter
        allOption
        className={className}
        containerClassName={containerClassName}
        label="Withdrawal Statuses"
        onChange={onWithdrawalStatusChange}
        options={statusOptions}
        selectedValue={withdrawalStatus}
        testId="withdrawal-status-filter"
      />
      <Filter
        allOption
        className={className}
        containerClassName={containerClassName}
        label="Withdrawal Methods"
        onChange={onWithdrawalMethodChange}
        options={methodOptions}
        selectedValue={withdrawalMethod}
        testId="withdrawal-method-filter"
      />
    </div>
  );
};
